import {
  ScriptFunctionInfo,
  TaskOutputReference,
  WorkcellInstrument,
  Workflow,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import { DriversByNameAndVersion } from '../../../../data/drivers';
import { SDKInteropError, SdkMisconfigurationError } from '../../../../errors';
import {
  API_DATA_EXPORT_INPUT_ID,
  LOCAL_DATA_EXPORT_INPUT_ID,
} from '../../../../features/workflowJsonSelectors/constants';
import { splitDriverNameAndVersion } from '../../../useInitializeWorkcellAndDrivers/utils';
import { getInstrumentForTask } from '../../helpers';

/**
 * Validates that an input ID corresponds to a valid argument name for the function
 * associated with a given task.
 *
 * This function performs validation for data dependency edges by:
 * 1. Finding the function associated with the task (by matching task.function.value to function_name)
 * 2. Ensuring the specified input ID matches one of the argument names in that function's schema
 *
 * @param inputId - The input identifier to validate against function arguments
 * @param task - The task (ActionTask, CodeTask, or DataConnector) that may have an associated function.
 *               Must have a 'function' property with a 'value' field containing the function name.
 * @param functions - Array of available function definitions with their argument schemas
 *
 * @returns `true` if the inputId matches an argument name in the task's associated function
 * @returns `null` if no function is found that matches the task's function name, or if the task
 *          doesn't have a 'function' property (e.g., ActionTask->ActionTask connections)
 *
 * @throws {SdkMisconfigurationError} When a matching function is found but the inputId doesn't
 *         match any of the function's argument names
 *
 * @example
 * ```typescript
 * const task = { function: { value: 'processData' }, id: 'task1' };
 * const functions = [{
 *   function_name: 'processData',
 *   arguments: [{ name: 'username' }, { name: 'data' }]
 * }];
 *
 * const result = validateInputId('username', task, functions);
 * // Returns true - 'username' is a valid argument for 'processData' function
 *
 * const result2 = validateInputId('invalid', task, functions);
 * // Throws SdkMisconfigurationError - 'invalid' is not an argument name
 * ```
 */
export const validateInputId = (
  inputId: string,
  task: Workflow['tasks'][string],
  functions: ScriptFunctionInfo[]
): true | null | never => {
  const associatedFunction = functions.find((fn) => {
    if (!('function' in task)) return false;
    return fn.function_name === task.function.value;
  });

  // is there any other handling we can apply to cases with no associated function?
  // ActionTask->ActionTask for example
  if (associatedFunction) {
    const argumentNames = associatedFunction.arguments.map(({ name }) => name);
    if (!argumentNames.includes(inputId)) {
      throw new SdkMisconfigurationError(
        `Data dependency input for task "${task.id}" has input ID "${inputId}" which does not match any argument names for function "${associatedFunction.function_name}"`
      );
    }
  } else {
    return null;
  }

  return true;
};

/**
 * Translates API `inputId` to local `inputId` for `data_connector` tasks
 * @param inputId
 * @param taskType
 * @returns string
 */
export const getTargetHandleName = (
  inputId: string,
  taskType: Workflow['tasks'][string]['type']
): string => {
  // API uses 'input_task' for data_connector tasks, locally we use 'data_key'
  if (taskType === 'data_connector' && inputId === API_DATA_EXPORT_INPUT_ID) {
    return LOCAL_DATA_EXPORT_INPUT_ID;
  } else {
    return inputId;
  }
};

export const getSourceHandleName = (
  sourceTask: Workflow['tasks'][string],
  input: TaskOutputReference,
  workcellInstruments: WorkcellInstrument[],
  driversByNameAndVersion: DriversByNameAndVersion
): string | never => {
  if (input.output_name) return input.output_name;

  if (sourceTask.type !== 'action') {
    throw new SdkMisconfigurationError(
      `Data dependency input for task "${sourceTask.id}" is missing output_name and source task is not an action task`
    );
  }

  const workcellInstrument = getInstrumentForTask(
    sourceTask,
    workcellInstruments
  );

  const { driverName, driverVersion } = splitDriverNameAndVersion(
    workcellInstrument.driver
  );

  const driverVersions = driversByNameAndVersion[driverName];
  if (!driverVersions) {
    throw new SDKInteropError(
      `Data dependency input for task "${sourceTask.id}" is missing output_name and source task's driver for instrument "${workcellInstrument.id}" could not be found.`
    );
  }

  const driver = driverVersions[driverVersion];
  if (!driver) {
    throw new SDKInteropError(
      `Data dependency input for task "${sourceTask.id}" is missing output_name and source task's driver for instrument "${workcellInstrument.id}" could not be found for version "${driverVersion}".`
    );
  }

  const action = driver.actions[sourceTask.action];
  if (!action) {
    throw new SDKInteropError(
      `Data dependency input for task "${sourceTask.id}" is missing output_name and action "${sourceTask.action}" could not be found in driver for instrument "${workcellInstrument.id}".`
    );
  }

  const [firstOutputName, ...otherOutputNames] = Object.keys(
    action.data_outputs
  );

  if (otherOutputNames.length > 0) {
    throw new SdkMisconfigurationError(
      `Data dependency input for task "${sourceTask.id}" is missing output_name and source task's driver for instrument "${workcellInstrument.id}" has multiple possible data outputs.`
    );
  }

  if (!firstOutputName) {
    throw new SDKInteropError(
      `Data dependency input for task "${sourceTask.id}" is missing output_name and source task's driver for instrument "${workcellInstrument.id}" has no data outputs.`
    );
  }

  return firstOutputName;
};

/**
 * Flattens a nested workflow task structure into a single-level object containing all tasks.
 *
 * This function recursively traverses nested task structures and returns a flattened
 * object where all tasks are accessible at the top level by their task IDs. It handles
 * tasks that contain nested sub-tasks by recursively processing their `tasks` property.
 *
 * @param tasks - The workflow tasks object to flatten. Can contain nested task structures
 *                where some tasks have a `tasks` property containing sub-tasks.
 *
 * @returns A flattened object containing all tasks (both top-level and nested) indexed by task ID
 *
 * @note Currently works for ContinuousLoading tasks but not Conditionals, as it reads
 *       `task.tasks` property but not `task.next` which is used by Conditional tasks.
 *
 * @example
 * ```typescript
 * const nestedTasks = {
 *   'task1': { id: 'task1', type: 'action' },
 *   'container': {
 *     id: 'container',
 *     type: 'continuous_loading',
 *     tasks: {
 *       'task2': { id: 'task2', type: 'code' },
 *       'task3': { id: 'task3', type: 'action' }
 *     }
 *   }
 * };
 *
 * const flattened = getFlatWorkflowTasks(nestedTasks);
 * // Returns: {
 * //   'task1': { id: 'task1', type: 'action' },
 * //   'container': { id: 'container', type: 'continuous_loading', tasks: {...} },
 * //   'task2': { id: 'task2', type: 'code' },
 * //   'task3': { id: 'task3', type: 'action' }
 * // }
 * ```
 */
export const getFlatWorkflowTasks = (tasks: Workflow['tasks']) => {
  return Object.values(tasks).reduce<Workflow['tasks']>((acc, task) => {
    const handleTask = (task: Workflow['tasks'][string]): void => {
      if ('tasks' in task) {
        Object.values(task.tasks).forEach(handleTask);
      }
      acc[task.id] = task;
    };

    handleTask(task);
    return acc;
  }, tasks);
};
